
import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Linkedin, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface SocialShareProps {
  psychologistId: string;
  psychologistName: string;
  className?: string;
}

const SocialShare = ({ psychologistId, psychologistName, className }: SocialShareProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const profileUrl = `${window.location.origin}/psychologists/${psychologistId}`;
  const encodedUrl = encodeURIComponent(profileUrl);
  const encodedText = encodeURIComponent(`Confira o perfil de ${psychologistName} na PsiOnline`);

  // PRD v92: Track social share events in database
  const trackShareEvent = async (platform: string) => {
    try {
      await supabase
        .from('share_events')
        .insert({
          psychologist_id: psychologistId,
          platform: platform
        });
    } catch (error) {
      // Optional analytics - don't block UI if it fails
      console.log('Share tracking failed:', error);
    }
  };

  const shareOptions = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: 'text-blue-600 hover:text-blue-700',
      ariaLabel: `Compartilhar perfil de ${psychologistName} no Facebook`
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      color: 'text-blue-400 hover:text-blue-500',
      ariaLabel: `Compartilhar perfil de ${psychologistName} no Twitter`
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: 'text-blue-700 hover:text-blue-800',
      ariaLabel: `Compartilhar perfil de ${psychologistName} no LinkedIn`
    }
  ];

  const handleShare = async (url: string, platform: string) => {
    window.open(url, '_blank', 'width=600,height=400');
    await trackShareEvent(platform.toLowerCase());
    setIsOpen(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      await trackShareEvent('copy_link');
      toast({
        title: "Link copiado!",
        description: "O link do perfil foi copiado para a área de transferência.",
      });
      
      setTimeout(() => {
        setCopied(false);
        setIsOpen(false);
      }, 1000);
    } catch (err) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o link. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`p-1 h-8 w-8 rounded-full hover:bg-gray-100 ${className}`}
          aria-label={`Compartilhar perfil de ${psychologistName}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Share2 className="h-4 w-4 text-gray-600" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="end">
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-900 mb-2 px-2">
            Compartilhar perfil
          </div>
          
          {shareOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <Button
                key={option.name}
                variant="ghost"
                size="sm"
                className="w-full justify-start h-8 px-2"
                onClick={() => handleShare(option.url, option.name)}
                aria-label={option.ariaLabel}
              >
                <IconComponent className={`h-4 w-4 mr-2 ${option.color}`} />
                <span className="text-sm">{option.name}</span>
              </Button>
            );
          })}
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start h-8 px-2"
            onClick={handleCopyLink}
            aria-label={`Copiar link do perfil de ${psychologistName}`}
          >
            {copied ? (
              <Check className="h-4 w-4 mr-2 text-green-600" />
            ) : (
              <Copy className="h-4 w-4 mr-2 text-gray-600" />
            )}
            <span className="text-sm">
              {copied ? 'Copiado!' : 'Copiar link'}
            </span>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SocialShare;
